import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Collapse } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionCard from './SectionCard';

interface FilterPanelProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  subtitleTranslationKey?: string;
  children: ReactNode;
  actions?: ReactNode;
  defaultExpanded?: boolean;
  collapsible?: boolean;
}

const FilterPanel = ({
  title,
  subtitle,
  subtitleTranslationKey,
  children,
  actions,
  defaultExpanded = true,
  collapsible = true,
}: FilterPanelProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const toggleButtonColor = theme.palette.mode === 'dark' ? 'secondary' : 'primary';
  const [expanded, setExpanded] = useState(defaultExpanded);
  const resolvedTitle = title ?? t('visual.filterPanel.title');
  const resolvedSubtitle =
    subtitle ?? (subtitleTranslationKey ? t(subtitleTranslationKey) : undefined);
  const toggleButton = collapsible ? (
    <Button
      color={toggleButtonColor}
      size="small"
      startIcon={<FilterAltIcon />}
      endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      onClick={() => setExpanded(value => !value)}
    >
      {expanded ? t('visual.filterPanel.hide') : t('visual.filterPanel.show')}
    </Button>
  ) : null;

  return (
    <Box data-tour="filter-panel">
      <SectionCard
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {actions}
            {toggleButton}
          </Box>
        }
      >
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </SectionCard>
    </Box>
  );
};

export default FilterPanel;
